import axios from 'axios';

// Configuração da API baseada no ambiente
const getApiUrl = () => {
  // Se VITE_API_URL estiver definida, usar ela (prioridade máxima)
  if (import.meta.env.VITE_API_URL) {
    let url = import.meta.env.VITE_API_URL;
    // Garantir que a URL termina com /api
    if (!url.endsWith('/api') && !url.endsWith('/api/')) {
      url = url.endsWith('/') ? `${url}api` : `${url}/api`;
    }
    return url;
  }
  
  // Em desenvolvimento, usar localhost
  if (import.meta.env.DEV) {
    return 'http://localhost:3001/api';
  }
  
  // Em produção, usar a URL padrão com porta 5016 (conforme health check)
  return 'https://api.hnp.com.br/api';
};

// Configuração base do axios
const api = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000, // Aumentado para 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
});

// API pública (sem autenticação)
const publicApi = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000, // Aumentado para 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar respostas
api.interceptors.response.use(
  (response) => {
    // Em desenvolvimento, logar respostas
    if (import.meta.env.DEV) {
      console.log('📡 Resposta da API:', response.config.url, response.data);
    }
    return response;
  },
  (error) => {
    // Sempre logar erros (mesmo em produção)
    console.error('❌ Erro na API:', error.config?.url, error.response?.data || error.message);
    
    // Tratar erros de autenticação (401) e token inválido (403)
    if (error.response?.status === 401) {
      // Token expirado ou inválido - remover dados do localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('selectedHotel');
      // Não fazer window.location.href - deixar o ProtectedRoute lidar com redirecionamento
    } else if (error.response?.status === 403 && error.response?.data?.message === 'Token inválido') {
      // Token inválido - remover dados e redirecionar para login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('selectedHotel');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Função para retry com delay exponencial
const retryRequest = async (fn, maxRetries = 3) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === maxRetries - 1) throw error;
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        const delay = Math.pow(2, i) * 1000; // Delay exponencial: 1s, 2s, 4s
        console.log(`🔄 Tentativa ${i + 1} falhou, tentando novamente em ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        throw error;
      }
    }
  }
};

// Serviços de autenticação
export const authService = {
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedHotel');
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  getSelectedHotel: () => {
    const hotel = localStorage.getItem('selectedHotel');
    return hotel ? JSON.parse(hotel) : null;
  },

  setSelectedHotel: (hotel) => {
    localStorage.setItem('selectedHotel', JSON.stringify(hotel));
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  }
};

// Serviços de reservas
export const reservationService = {
  getAll: async (params = {}) => {
    console.log('🔍 reservationService.getAll chamado com params:', params);
    const response = await api.get('/reservations', { params });
    console.log('📊 Resposta bruta da API:', response);
    console.log('📊 response.data:', response.data);
    console.log('📊 response.data.reservations:', response.data.reservations);
    return response.data;
  },

  getById: async (id, params = {}) => {
    const response = await api.get(`/reservations/${id}`, { params });
    return response.data;
  },

  create: async (reservationData, params = {}) => {
    const response = await api.post('/reservations', reservationData, { params });
    return response.data;
  },

  update: async (id, reservationData, params = {}) => {
    const response = await api.put(`/reservations/${id}`, reservationData, { params });
    return response.data;
  },

  delete: async (id, params = {}) => {
    const response = await api.delete(`/reservations/${id}`, { params });
    return response.data;
  },

  checkIn: async (id, params = {}) => {
    const response = await api.post(`/reservations/${id}/check-in`, {}, { params });
    return response.data;
  },

  checkOut: async (id, params = {}) => {
    const response = await api.post(`/reservations/${id}/check-out`, {}, { params });
    return response.data;
  }
};

// Serviços de quartos
export const roomService = {
  getAll: async (params = {}) => {
    try {
      console.log('🔍 roomService.getAll chamado com params:', params);
      const response = await api.get('/rooms', { params });
      console.log('🔍 roomService.getAll resposta:', response);
      return response.data;
    } catch (error) {
      console.error('🔍 roomService.getAll erro:', error);
      throw error;
    }
  },

  getById: async (id) => {
    const response = await api.get(`/rooms/${id}`);
    return response.data;
  },

  getAvailability: async (date, hotelId) => {
    try {
      const params = { date };
      if (hotelId) params.hotelId = hotelId;
      console.log('🔍 roomService.getAvailability chamado com params:', params);
      
      return await retryRequest(async () => {
        const response = await api.get('/rooms/availability', { params });
        const data = response.data;
        if (data?.[0] && import.meta.env.DEV) {
          console.log('🛏️ roomService.getAvailability - primeiro quarto (beds):', {
            number: data[0].number,
            beds: data[0].beds,
            bedsSolteiro: data[0].bedsSolteiro,
            bedsCasal: data[0].bedsCasal
          });
        }
        return data;
      });
    } catch (error) {
      console.error('🔍 roomService.getAvailability erro:', error);
      throw error;
    }
  },

  create: async (roomData) => {
    const response = await api.post('/rooms', roomData);
    return response.data;
  },

  update: async (id, roomData) => {
    const response = await api.put(`/rooms/${id}`, roomData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/rooms/${id}`);
    return response.data;
  },

  getRoomTypes: async () => {
    try {
      console.log('🔍 roomService.getRoomTypes chamado');
      const response = await api.get('/rooms/types');
      console.log('🔍 roomService.getRoomTypes resposta:', response);
      return response.data;
    } catch (error) {
      console.error('🔍 roomService.getRoomTypes erro:', error);
      throw error;
    }
  }
};

// Serviços de tipos de quarto
export const roomTypeService = {
  getAll: async (params = {}) => {
    try {
      console.log('🔍 roomTypeService.getAll chamado com params:', params);
      const response = await api.get('/room-types', { params });
      console.log('🔍 roomTypeService.getAll resposta:', response);
      return response.data;
    } catch (error) {
      console.error('🔍 roomTypeService.getAll erro:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      console.log('🔍 roomTypeService.getById chamado com id:', id);
      const response = await api.get(`/room-types/${id}`);
      console.log('🔍 roomTypeService.getById resposta:', response);
      return response.data;
    } catch (error) {
      console.error('🔍 roomTypeService.getById erro:', error);
      throw error;
    }
  },

  create: async (roomTypeData) => {
    try {
      console.log('🔍 roomTypeService.create chamado com data:', roomTypeData);
      const response = await api.post('/room-types', roomTypeData);
      console.log('🔍 roomTypeService.create resposta:', response);
      return response.data;
    } catch (error) {
      console.error('🔍 roomTypeService.create erro:', error);
      throw error;
    }
  },

  update: async (id, roomTypeData) => {
    try {
      console.log('🔍 roomTypeService.update chamado com id:', id, 'data:', roomTypeData);
      const response = await api.put(`/room-types/${id}`, roomTypeData);
      console.log('🔍 roomTypeService.update resposta:', response);
      return response.data;
    } catch (error) {
      console.error('🔍 roomTypeService.update erro:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      console.log('🔍 roomTypeService.delete chamado com id:', id);
      const response = await api.delete(`/room-types/${id}`);
      console.log('🔍 roomTypeService.delete resposta:', response);
      return response.data;
    } catch (error) {
      console.error('🔍 roomTypeService.delete erro:', error);
      throw error;
    }
  }
};

// Serviços de hotéis
export const hotelService = {
  getAll: async () => {
    const timestamp = new Date().getTime();
    console.log('🔍 Chamando hotelService.getAll()...');
    
    return retryRequest(async () => {
      const response = await api.get(`/hotels?t=${timestamp}`);
      console.log('📊 Resposta do hotelService.getAll():', response.data);
      return response.data;
    });
  },

  getById: async (id) => {
    return retryRequest(async () => {
      const response = await api.get(`/hotels/${id}`);
      return response.data;
    });
  },

  create: async (hotelData) => {
    const response = await api.post('/hotels', hotelData);
    return response.data;
  },

  update: async (id, hotelData) => {
    const response = await api.put(`/hotels/${id}`, hotelData);
    return response.data;
  },

  // Hotel settings
  getSetting: async (hotelId, key) => {
    const response = await api.get(`/hotels/${hotelId}/settings/${key}`);
    return response.data;
  },

  setSetting: async (hotelId, key, value, type = 'NUMBER', description = undefined) => {
    const response = await api.put(`/hotels/${hotelId}/settings/${key}`, { value, type, description });
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/hotels/${id}`);
    return response.data;
  },

  // Método para limpar cache de hotéis
  clearCache: async () => {
    try {
      console.log('🧹 Limpando cache de hotéis...');
      const response = await api.post('/hotels/clear-cache');
      console.log('✅ Cache limpo:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao limpar cache:', error);
      throw error;
    }
  }
};

// Serviço público de hotéis (sem autenticação)
export const publicHotelService = {
  getAll: async () => {
    const timestamp = new Date().getTime();
    console.log('🔍 Chamando publicHotelService.getAll()...');
    
    return retryRequest(async () => {
      const response = await publicApi.get(`/hotels/public?t=${timestamp}`);
      console.log('📊 Resposta do publicHotelService.getAll():', response.data);
      return response.data;
    });
  },

  getByCode: async (code, checkInDate = null, checkOutDate = null) => {
    return retryRequest(async () => {
      const params = {};
      // Só envia datas quando ambas forem fornecidas (LP atualiza disponibilidade ao clicar em Buscar Quartos)
      if (checkInDate && checkOutDate) {
        params.checkInDate = checkInDate;
        params.checkOutDate = checkOutDate;
      }
      const response = await publicApi.get(`/hotels/public/${code}`, { params });
      return response.data;
    });
  }
};

// Serviços de dashboard
export const dashboardService = {
  getStats: async (params = {}) => {
    return retryRequest(async () => {
      const response = await api.get('/dashboard/stats', { params });
      return response.data;
    });
  },

  getRecentActivity: async (params = {}) => {
    return retryRequest(async () => {
      const response = await api.get('/dashboard/recent-activity', { params });
      return response.data;
    });
  },

  getUpcomingArrivals: async (params = {}) => {
    return retryRequest(async () => {
      const response = await api.get('/dashboard/upcoming-arrivals', { params });
      return response.data;
    });
  }
};

// Serviços financeiros
export const financialService = {
  // Buscar resumo financeiro
  getSummary: async (params = {}) => {
    return retryRequest(async () => {
      const response = await api.get('/transactions/summary', { params });
      return response.data;
    });
  },

  // Buscar dados para gráficos
  getCharts: async (params = {}) => {
    return retryRequest(async () => {
      const response = await api.get('/transactions/charts', { params });
      return response.data;
    });
  },

  // Buscar transações
  getTransactions: async (params = {}) => {
    return retryRequest(async () => {
      const response = await api.get('/transactions', { params });
      return response.data;
    });
  },

  // Buscar categorias de despesas
  getExpenseCategories: async (params = {}) => {
    return retryRequest(async () => {
      const response = await api.get('/transactions/expense-categories', { params });
      return response.data;
    });
  },

  // Criar transação
  createTransaction: async (data) => {
    return retryRequest(async () => {
      const response = await api.post('/transactions', data);
      return response.data;
    });
  },

  // Atualizar transação
  updateTransaction: async (id, data) => {
    return retryRequest(async () => {
      const response = await api.put(`/transactions/${id}`, data);
      return response.data;
    });
  },

  // Deletar transação
  deleteTransaction: async (id) => {
    return retryRequest(async () => {
      const response = await api.delete(`/transactions/${id}`);
      return response.data;
    });
  }
};

// Serviço de gerenciamento de acesso de usuários
export const userAccessService = {
  // Buscar todos os acessos
  getAll: async () => {
    return retryRequest(async () => {
      const response = await api.get('/user-access');
      return response.data;
    });
  },

  // Buscar estatísticas
  getStats: async () => {
    return retryRequest(async () => {
      const response = await api.get('/user-access/stats');
      return response.data;
    });
  },

  // Criar acesso
  create: async (data) => {
    return retryRequest(async () => {
      const response = await api.post('/user-access', data);
      return response.data;
    });
  },

  // Atualizar acesso
  update: async (id, data) => {
    return retryRequest(async () => {
      const response = await api.put(`/user-access/${id}`, data);
      return response.data;
    });
  },

  // Deletar acesso
  delete: async (id) => {
    return retryRequest(async () => {
      const response = await api.delete(`/user-access/${id}`);
      return response.data;
    });
  }
};

export default api;
export { api, publicApi }; 